import { User, Building, Layout } from 'lucide-react';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

export type SkillScope = 'user' | 'tenant' | 'template';

export const scopeOptions = (t: TFunction<'pleri/skills'>) =>
  [
    {
      value: 'user' as const,
      label: t('personal'),
      sublabel: t('justForMe'),
      description: t('onlyVisibleToYouPleriCanHelp'),
      icon: User,
      adminOnly: false,
      color: 'orange' as const,
    },
    {
      value: 'tenant' as const,
      label: t('common:tenant'),
      sublabel: t('wholeOrganisation'),
      description: t('visibleToEveryoneInYourTenantOnly'),
      icon: Building,
      adminOnly: true,
      color: 'orange' as const,
    },
    {
      value: 'template' as const,
      label: t('template'),
      sublabel: t('shareableBlueprint'),
      description: t('everyoneCanSeeItOthersMustFork'),
      icon: Layout,
      adminOnly: false,
      color: 'blue' as const,
    },
  ] as const;

interface ScopeSelectorProps {
  value: SkillScope;
  onChange: (scope: SkillScope) => void;
  isAdmin?: boolean;
  excludeTemplate?: boolean;
}

export function ScopeSelector({ value, onChange, isAdmin = false, excludeTemplate = false }: ScopeSelectorProps) {
  const { t } = useTranslation('pleri/skills');
  return (
    <div>
      <p className="mb-3 text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
        {t('whoIsThisSkillFor')}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {scopeOptions(t).filter((opt) => (!opt.adminOnly || isAdmin) && !(excludeTemplate && opt.value === 'template')).map((opt) => {
          const Icon = opt.icon;
          const isSelected = value === opt.value;
          const isBlue = opt.value === 'template';
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`flex flex-col items-start gap-2 rounded-xl border-2 p-3.5 text-left transition-all duration-150 ${
                isSelected
                  ? isBlue
                    ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-950/40'
                    : 'border-orange-400 bg-orange-50 dark:border-orange-600 dark:bg-orange-950/40'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex w-full items-center justify-between">
                <Icon
                  className={`h-4 w-4 ${
                    isSelected
                      ? isBlue ? 'text-blue-500' : 'text-orange-500'
                      : 'text-gray-400'
                  }`}
                />
                <span
                  className={`h-4 w-4 flex-shrink-0 rounded-full border-2 transition-colors ${
                    isSelected
                      ? isBlue ? 'border-blue-500 bg-blue-500' : 'border-orange-500 bg-orange-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {isSelected && (
                    <span className="flex h-full w-full items-center justify-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    </span>
                  )}
                </span>
              </div>
              <div>
                <p className={`text-sm font-semibold ${
                  isSelected
                    ? isBlue ? 'text-blue-800 dark:text-blue-200' : 'text-orange-800 dark:text-orange-200'
                    : 'text-gray-700 dark:text-gray-200'
                }`}>
                  {opt.label}
                </p>
                <p className={`mt-0.5 text-xs ${
                  isSelected
                    ? isBlue ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {opt.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
